import { defaultBlockAt } from '@common/prosemirror/commands/commands.prosemirror';
import { closestBlockAncestor, deepestBlocksBetween, FoundNodeInfo } from '@common/prosemirror/model/node-find';
import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copyFragmentWithChanges, copyNodeWithChanges, copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Attrs, Fragment, Node, NodeType, ResolvedPos, Slice } from 'prosemirror-model';
import { AllSelection, EditorState, NodeSelection, Selection, TextSelection, Transaction } from 'prosemirror-state';
import { canSplit, ReplaceStep } from 'prosemirror-transform';
import { EditorView } from 'prosemirror-view';

type SplitType = (null | { type: NodeType, attrs?: Attrs | null });

/**
 * Options for the split helper function.
 */
interface SplitOptions {
  /** Whether to split at the end of the node. */
  atEnd: boolean;
  /** Whether to split at the start of the node. */
  atStart: boolean;
  /** The depth into the document where the split is being applied. */
  depth: number;
  /** The default block node type for the schema. Used to replace the nodes made from the split in some circumstances. */
  defaultBlockNodeType: NodeType;
  /** The node type for the mc-central-container node. */
  mcCentralContainerNodeType?: NodeType;
  /** The absolute position into the document where the split is being applied. */
  pos: number;
  /** The depth of the slice being created from the split. */
  sliceDepth: number;
  /** The types to split the node into. */
  typesAfter?: SplitType[];
}

/**
 * Splits the block node at the current selection adding tracked changes if they are enabled.
 * Taken from prosemirror-transform and modified to support tracked changes and mc-central-container.
 * @param tr The transaction to add the split to.
 * @param trackedChangesState The tracked changes plugin state.
 * @param options The options for the split.
 */
export function split(tr: Transaction, trackedChangesState: TrackedChangesPluginState, options?: SplitOptions) {
  let isSplitAtStart: boolean = !options.atEnd && options.atStart;
  const selection: Selection = tr.selection;
  const $from: ResolvedPos = selection.$from;
  let pos: number = options.pos;

  if (trackedChangesState?.enabled) {
    const $pos: ResolvedPos = tr.doc.resolve(pos);
    // Use to check if block is mcCentralContainer
    const block: FoundNodeInfo = closestBlockAncestor(selection.$head);
    // Use to count the number of blocks in the selection
    const blocks: FoundNodeInfo[] = deepestBlocksBetween($from, selection.$to);

    const blockInnerDepth: number = $pos.depth - options.sliceDepth + 1;
    const blockStart: ResolvedPos = tr.doc.resolve($pos.start(blockInnerDepth));
    const blockEnd: ResolvedPos = tr.doc.resolve($pos.end(blockInnerDepth));
    const blockBefore: ResolvedPos = tr.doc.resolve($pos.before(blockInnerDepth));
    let blockAfter: ResolvedPos = tr.doc.resolve($pos.after(blockInnerDepth));

    // Account for the selection sometimes being at the beginning or end of an inline node. Calculate the offset to reach before/after the inline.
    let openStart: number = 0;
    let openEnd: number = 0;
    for (let d = $pos.depth, e = $pos.depth - options.sliceDepth, i = options.sliceDepth - 1; d > e; d--, i--) {
      const node: Node = $pos.node(d);
      // Check if split position is at the start of the node
      const isAtStart: boolean = $pos.start(d) === $pos.pos - ($pos.depth - d);
      // Don't include empty inline nodes
      if (isAtStart && !node.isBlock) {
        openStart++;
      }
    }

    for (let d = selection.$to.depth, e = selection.$to.depth - options.sliceDepth, i = options.sliceDepth - 1; d > e; d--, i--) {
      const node: Node = selection.$to.node(d);
      // Check if split position is at the end of the node
      const isAtEnd: boolean = selection.$to.end(d) === selection.to + (selection.$to.depth - d);
      // Don't include empty inline nodes
      if (isAtEnd && !node.isBlock) {
        openEnd++;
      }
    }

    /* Selection is a block selection*/
    if (selection instanceof NodeSelection && selection.node.isBlock) {
      // The block and siblings after
      const rightContent: Fragment = tr.doc.slice(selection.from, $pos.end($pos.depth)).content;

      // Set the block and siblings after as deleted
      const trackedRemove: Fragment = copyFragmentWithChanges(trackedChangesState.changeMarkType, rightContent, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
      tr.replaceWith(selection.from, $pos.end($pos.depth), trackedRemove);

      // Create a new parent node with the right content as added
      const rightTrackedAddBlock: Node = block.node.type.create(trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add), copyFragmentWithChanges(trackedChangesState.changeMarkType, rightContent, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add)));

      // Add the block and siblings as add changes after the parent block
      tr.insert(blockAfter.pos, rightTrackedAddBlock);

      // Select the original block node
      tr.setSelection(NodeSelection.create(tr.doc, blockAfter.pos + 1));
    }
    /* Selection is not in the same block */
    else if (blocks.length >= 2) {
      // Replace the selection with a delete change copy
      const remSlice: Slice = copySliceWithChanges(trackedChangesState.changeMarkType, tr.doc.slice(selection.from, selection.to), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
      tr.replaceRange(selection.from, selection.to, remSlice);
    }
    /* Block is a central container. Split into two default blocks */
    else if (block.node.type === options?.mcCentralContainerNodeType) {
      const leftChildContent: Fragment = tr.doc.slice(blockStart.pos, selection.from - openStart).content;
      // Don't delete the node selection
      const rightChildContent: Fragment = tr.doc.slice(selection instanceof NodeSelection ? selection.from - openStart : selection.to + openEnd, blockEnd.pos).content;
      // Replace the block with a delete change copy
      const remSlice: Slice = copySliceWithChanges(trackedChangesState.changeMarkType, tr.doc.slice(blockBefore.pos, blockAfter.pos), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
      tr.replace(blockBefore.pos, blockAfter.pos, remSlice);
      // Keep the add tracked changes synced to the same transaction
      const addChanges = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add);
      // Add two default blocks with the content from the beginning of the block to the beginning of the selection and the end of the selection to the end of the block
      let leftBlock: Node = options.defaultBlockNodeType.create(null, leftChildContent);
      leftBlock = copyNodeWithChanges(trackedChangesState.changeMarkType, leftBlock, addChanges);
      let rightBlock: Node = options.defaultBlockNodeType.create(null, rightChildContent);
      rightBlock = copyNodeWithChanges(trackedChangesState.changeMarkType, rightBlock, addChanges);
      tr.insert(blockBefore.pos, Fragment.from([leftBlock, rightBlock]));
      // Selection starts from the beginning of the split block
      const afterOffset = getAfterOffset(rightBlock);
      if (selection instanceof NodeSelection) {
        // Select the original node
        tr.setSelection(NodeSelection.create(tr.doc, blockBefore.pos + leftBlock.nodeSize + afterOffset - 1));
      } else {
        tr.setSelection(Selection.near(tr.doc.resolve(blockBefore.pos + leftBlock.nodeSize + afterOffset)));
      }
    }
    /* Selection starts at the beginnging of the block */
    else if (isSplitAtStart) {
      // Replace the beginning of the block with a delete change copy
      if (!(tr.selection.empty || selection instanceof NodeSelection)) {
        let remSlice: Slice = tr.doc.slice(blockStart.pos, selection.to + openEnd);
        if (remSlice.size) {
          remSlice = copySliceWithChanges(trackedChangesState.changeMarkType, tr.doc.slice(blockStart.pos, selection.to + openEnd), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
          tr.replace(blockStart.pos, selection.to + openEnd, remSlice);
        }
      }
      // Insert a empty default add block before the block
      const leftAddBlock: Node = options.defaultBlockNodeType.create(trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add));
      tr.insert(blockBefore.pos, leftAddBlock);
      // Cursor moves to start of block
      tr.setSelection(Selection.near(tr.doc.resolve(tr.mapping.map(blockBefore.pos)), -1));
    }
    /* Split is in the middle or end of the block*/
    else {
      // The right content is from the beginning of the selection (if is a node selection), or end of the selection to the end of the block
      let rightContent: Slice = selection instanceof NodeSelection ? tr.doc.slice(selection.from - openStart, blockEnd.pos) : tr.doc.slice(selection.to + openEnd, blockEnd.pos);
      const leftContent: Slice = tr.doc.slice(selection.from - openStart, blockAfter.pos);

      // Replace the beginning of the selection to the end of the block node with a delete change copy
      if (leftContent.size > leftContent.openStart) {
        const remSlice: Slice = copySliceWithChanges(trackedChangesState.changeMarkType, leftContent, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove));
        if (remSlice.size) {
          tr.replace(selection.from - openStart, blockAfter.pos, remSlice);
        } else {
          tr.deleteRange(selection.from - openStart, blockEnd.pos);
        }
      }
      // Remap to account for the changed block content
      blockAfter = tr.doc.resolve(tr.doc.resolve(tr.mapping.map(pos)).after(blockInnerDepth));
      // Insert a new block after with content from the selection to the end of the block node with an add change
      const blockType: NodeType = options.atEnd ? options.defaultBlockNodeType : block.node.type;

      // Create the new block with the old right content
      let rightAddBlock = blockType.create(null, rightContent.content);
      // Add changes to the block
      rightAddBlock = copyNodeWithChanges(trackedChangesState.changeMarkType, rightAddBlock, trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add));
      tr.insert(blockAfter.pos, rightAddBlock);

      // Cursor moves to start of block
      const afterOffset = getAfterOffset(rightAddBlock);
      if (selection instanceof NodeSelection) {
        // Select the original node
        tr.setSelection(NodeSelection.create(tr.doc, blockAfter.pos + afterOffset - 1));
      } else {
        tr.setSelection(Selection.near(tr.doc.resolve(blockAfter.pos + afterOffset)));
      }
    }

    // Mark that the changes were tracked
    tr.setMeta(TrackedChangesTrackedMetaKey, true);
    return;
  } else {
    // Delete the selection
    if (selection instanceof TextSelection || selection instanceof AllSelection) {
      tr.deleteSelection();
      pos = tr.mapping.map(pos);
    }

    const $pos: ResolvedPos = tr.doc.resolve(pos);

    // Build the slice to insert that will cause the node to split
    let before: Fragment = Fragment.empty;
    let after: Fragment = Fragment.empty;

    // Account for the selection sometimes being at the beginning or end of an inline node. Calculate the offset to reach before/after the inline.
    let openStart: number = 0;
    let openEnd: number = 0;
    for (let d = $pos.depth, e = $pos.depth - options.sliceDepth, i = options.sliceDepth - 1; d > e; d--, i--) {
      const node = $pos.node(d);
      // Check if split position is at the start of the node
      const isAtStart = $pos.start(d) === $pos.pos - ($pos.depth - d);
      // Don't include empty inline nodes
      if (isAtStart && !node.isBlock) {
        openStart++;
        before = Fragment.from(null);
      } else {
        // Use the standard PM flow
        before = Fragment.from(node.copy(before));
      }

      // Check if split position is at the end of the node
      const isAtEnd: boolean = $pos.end(d) === $pos.pos + ($pos.depth - d);
      // Don't include empty inline nodes
      if (isAtEnd && !node.isBlock) {
        openEnd++;
        after = Fragment.from(null);
      } else {
        // Use the standard PM flow
        let typeAfter = options?.typesAfter && options.typesAfter[i];
        after = Fragment.from(typeAfter ? typeAfter.type.create(typeAfter.attrs, after) : node.copy(after));
      }
    }

    // Perform the split, removing hanging inline nodes
    tr.step(new ReplaceStep(pos - openStart, pos + openEnd, new Slice(before.append(after), options.sliceDepth - openStart, options.sliceDepth - openEnd)));

    // Grab the node that was split to see if the new nodes need to be replaced with the default node type
    const splitNode: Node = $from.node(options.depth);

    // Check if the first node should be replaced with the default node type. This can happen when the cursor was at the start of the node
    if (options.atStart && !options.atEnd && splitNode.type !== options.defaultBlockNodeType) {
      const firstPos: number = tr.mapping.map($from.before(options.depth));
      const $first: ResolvedPos = tr.doc.resolve(firstPos);

      if ($from.node(options.depth - 1).canReplaceWith($first.index(), $first.index() + 1, options.defaultBlockNodeType)) {
        tr.setNodeMarkup(firstPos, options.defaultBlockNodeType);
      }
    }

    // Check if the split node should be replaced with the default node type. This can happen when splitting plain text (aka mcCentralContainer)
    if (!options.atStart && splitNode.type === options?.mcCentralContainerNodeType) {
      tr.setNodeMarkup(tr.mapping.map($from.before(options.depth)), options.defaultBlockNodeType);
    }

    // Manually set the selection if it's a split start
    if (isSplitAtStart) {
      tr.setSelection(Selection.near(tr.doc.resolve(options.pos - /* Block node border */ 1 - (tr.selection.$from.depth - options.depth))));
    }
    // Manually set selection
    else if (tr.selection.from === pos) {
      tr.setSelection(Selection.near(tr.doc.resolve(pos + /* Block node border */ 1 + (tr.selection.$from.depth - options.depth))));
    }
  }
}

/**
 * Get the offset from the end of the original block to the beginning of the next block's text or first deepest inline node
 * @param node The block after
 * @returns The offset into the block after's first inline node or text
 */
function getAfterOffset(node: Node): number {
  // Account for the block border
  let afterOffset = 1;
  // Descend into the first child until we hit the leaf node
  for (let n = node; n.firstChild && !n.firstChild.isLeaf; n = n.firstChild) {
    afterOffset++;
  }
  return afterOffset;
}

export type SplitBlockCommand = (state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView) => boolean;

/**
 * Creates a variant of splitBlock that uses a custom function to determine the type of the newly split off block.
 * The returned command splits the block node at the current selection. Splitting any inline nodes at the selection.
 * Taken from prosemirror-command and modified to support tracked changes and mc-central-container.
 * @param splitNode A function that takes the current node and returns the node type to split the block as.
 * @returns A command that splits the block node at the current selection.
 */
export function splitBlockAs(splitNode?: (node: Node, atStart: boolean, atEnd: boolean, $from: ResolvedPos, defaultBlockNodeType: NodeType) => SplitType): SplitBlockCommand {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const trackedChangesState = getTrackedChangesState(state);

    let { $from, $to } = state.selection;
    if (state.selection instanceof NodeSelection && state.selection.node.isBlock) {
      if (!$from.parentOffset || !canSplit(state.doc, $from.pos)) {
        return false;
      }

      if (dispatch) {
        if (trackedChangesState?.enabled) {
          const tr = state.tr;
          split(tr, trackedChangesState, {
            atEnd: false,
            atStart: false,
            defaultBlockNodeType: null,
            depth: 1,
            pos: $from.pos,
            sliceDepth: 1
          });

          tr.setMeta(TrackedChangesTrackedMetaKey, true);

          dispatch(tr.scrollIntoView());
        } else {
          dispatch(state.tr.split($from.pos).scrollIntoView());
        }
      }

      return true;
    }

    if (!$from.depth) {
      return false;
    }

    let types: SplitType[] = [];
    let splitDepth: number;
    let deflt: NodeType;
    let atEnd = false;
    let atStart = false;

    for (let d = $from.depth; ; d -= 1) {
      const node = $from.node(d);

      if (node.isBlock) {
        atEnd = $from.end(d) === $from.pos + ($from.depth - d);
        atStart = $from.start(d) === $from.pos - ($from.depth - d);
        deflt = defaultBlockAt($from.node(d - 1).contentMatchAt($from.indexAfter(d - 1)));
        const splitType = splitNode && splitNode(node, atStart, atEnd, $from, deflt);
        types.unshift(splitType || (atEnd && deflt ? { type: deflt } : null));
        splitDepth = d;
        break;
      } else {
        if (d === 1) {
          return false;
        }

        types.unshift(null);
      }
    }

    if (dispatch) {
      const tr = state.tr

      const splitPos = tr.mapping.map($from.pos);
      let can = canSplit(tr.doc, splitPos, types.length, types);
      if (!can) {
        types[0] = deflt ? { type: deflt } : null;
        can = canSplit(tr.doc, splitPos, types.length, types);
      }

      // If the node still can't be split, do nothing
      if (!can) {
        return false;
      }

      // Split the node
      split(tr, trackedChangesState, {
        atEnd,
        atStart,
        defaultBlockNodeType: deflt,
        depth: splitDepth,
        mcCentralContainerNodeType: state.schema.nodes.mcCentralContainer,
        pos: splitPos,
        sliceDepth: types.length,
        typesAfter: types
      });

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        tr.setMeta(TrackedChangesTrackedMetaKey, true);
      }

      dispatch(tr.scrollIntoView());
    }

    return true;
  }
}

/**
 * Splits the block node at the current selection. Splitting any inline nodes at the selection.
 * @returns A command that splits the block node at the current selection.
 */
export const splitBlock: SplitBlockCommand = splitBlockAs((node, atStart, atEnd, $from, defaultBlockNodeType) => {
  // Always convert passthrough block nodes (mcCentralContainer) to default block nodes
  if (node.type.spec.passthroughBlock) {
    return { type: defaultBlockNodeType };
  }
});
